import { inject, Injectable } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { lastValueFrom } from 'rxjs';
import { filter, first, mergeMap } from 'rxjs/operators';
import { ModalCloseReason, ModalService, RouterBaseService } from '@tdwb/core';
import { RouterScrollService } from './router-scroll.service';
import { RouterStatusService } from './router-status.service';
import type {
  HookResult,
  Ng2StateDeclaration,
  RawParams,
  StateDeclaration,
  Transition,
  TransitionService
} from '@uirouter/angular';
import type { ModalOptions, ModalRef } from '@tdwb/core';

interface ModalRouteData {
  modalRef: ModalRef<unknown>;
  lastPageState: Ng2StateDeclaration;
  lastPageStateParams: RawParams;
}

/**
 * Handles modal state transitions (all states with name starting with 'modal'). opening and closing modals
 */
@Injectable({
  providedIn: 'root'
})
export class RouterModalService {

  private modalRouteData: ModalRouteData = { modalRef: undefined, lastPageState: undefined, lastPageStateParams: undefined };
  private readonly modalService = inject(ModalService);
  private readonly stateService = inject(StateService);
  private readonly routerStatusService = inject(RouterStatusService);
  private readonly routerScrollService = inject(RouterScrollService);
  private readonly routerService = inject(RouterBaseService);
  private readonly parent = inject(RouterModalService, { optional: true, skipSelf: true });

  static initHooks(transitionService: TransitionService): void {
    // transition handler opening and closing modal states in the modal dialog
    const onStartHook = (transition: Transition): HookResult | void => {
      const routerModalService: RouterModalService = transition.injector().get(RouterModalService);
      return routerModalService.onStartModalStateTransition(transition);
    };
    const onBeforeHook = (transition: Transition): HookResult | void => {
      const routerModalService: RouterModalService = transition.injector().get(RouterModalService);
      return routerModalService.onBeforeModalStateTransition(transition);
    };

    transitionService.onBefore({ to: 'modal.**' }, onBeforeHook, { invokeLimit: 1 });
    transitionService.onStart({ to: 'modal.**' }, onStartHook);
    transitionService.onStart({ from: 'modal.**', to: 'page.**' }, onStartHook);
  }

  /**
   * Waits for background page to load before opening modal
   *
   * @param transition
   * @returns
   */
  private onBeforeModalStateTransition(transition: Transition): HookResult | void {
    const targetModalState = transition.targetState();
    const fromModalState = this.isModalState(transition.from());
    const isPageTransitionFinished = this.routerStatusService.isPageTransitionFinished();
    const isTransient = this.routerStatusService.isTransient(targetModalState.name());

    if (!fromModalState && !isPageTransitionFinished && !isTransient) {
      const promise = lastValueFrom(this.routerStatusService.onPageTransition
        .pipe(
          filter((value: boolean): boolean => value),
          first(),
          mergeMap((): Promise<void> =>
            // this activates when RouterStatusService sends true in the onSuccess handler it defines.
            // it will finish the current page transition by returning a resolved Promise and schedule a transition
            // to the original modal state
            new Promise<void>((): void => {
              this.stateService.go(targetModalState.name(), targetModalState.params());
            })
          )
        ));
      // initiate transition if it has not been started yet
      if (this.routerStatusService.isPageTransitionFinished() === undefined) {
        // transition to the root - will cause ui router redirect to the initial state
        transition.router.urlService.url('/#');
      }
      // suspend transition until transition to the start page complete
      return promise;
    }
  }

  /**
   * The 'onStart' hook opens and closes modals.
   *
   * @param transition intercepted at 'onStart' lifecycle step
   * @returns response can be one of the following: boolean | TargetState | void | Promise<boolean | TargetState | void>
   */
  private onStartModalStateTransition(transition: Transition): void {
    const fromState: Ng2StateDeclaration = this.stateService.current;
    const fromParams: RawParams = this.routerService.getStateParams(false);
    const toState: Ng2StateDeclaration = transition.to();
    const fromModalState = this.isModalState(fromState);
    const toModalState = this.isModalState(toState);
    const modalRouteData = this.getModalRouteData();

    if (!fromModalState && toModalState) { // page to modal
      // we are on a page. save current state data and open modal right away
      this.setModalRouteData({
        lastPageState: fromState,
        lastPageStateParams: fromParams,
        modalRef: this.openModalDialog(transition, fromState, fromParams)
      });
    } else if (fromModalState && !toModalState) { // modal to page
      if (modalRouteData.lastPageState !== undefined && modalRouteData.lastPageState !== toState) {
        this.routerScrollService.scrollToTop();
      }
      if (modalRouteData.modalRef !== undefined && modalRouteData.modalRef.getResult() === undefined) {
        modalRouteData.modalRef.close(ModalCloseReason.RETURN_TO_BACKGROUND_PAGE);
      }
    } else if (fromModalState && toModalState) { // modal to modal
      if (this.isTransitionToNextModal(fromState, toState)) {
        // close currently open modal and open next modal
        if (modalRouteData.modalRef !== undefined) {
          modalRouteData.modalRef.close(ModalCloseReason.TRANSITION_TO_NEXT_MODAL);
        }
        modalRouteData.modalRef = this.openModalDialog(transition, modalRouteData.lastPageState, modalRouteData.lastPageStateParams);
      }
    }
  }

  /**
   * Returns true when transitioning between direct children of the 'modal.' state
   * e.g. modal.parent.child1 -> modal.parent.child2
   *
   * @param fromState
   * @param toState
   */
  private isTransitionToNextModal(fromState: Ng2StateDeclaration, toState: Ng2StateDeclaration): boolean {
    if (fromState === toState) {
      return false;
    }
    const fromStates: Array<string> = fromState.name.split('.');
    const toStates: Array<string> = toState.name.split('.');
    // from quote to buy/sell. NOT from quote.single to quote.multi
    const isNextModalDiffersFromPrevious = fromStates[1] !== toStates[1] ||
      this.getModalComponent(fromState) !== this.getModalComponent(toState);
    return isNextModalDiffersFromPrevious;
  }

  private getModalComponent(state: Ng2StateDeclaration): Record<string, unknown> {
    if (state.data !== undefined &&
      state.data.modalOptions !== undefined) {
      return state.data.modalOptions.component;
    }
    return undefined;
  }

  /**
   * Opens modal dialog saving current state which will be returned to when modal is closed.
   *
   * @param transition
   */
  private openModalDialog(transition: Transition, previousState: Ng2StateDeclaration, previousStateParams: RawParams): ModalRef<unknown> {
    const toState: Ng2StateDeclaration = transition.to();
    const modalOptions = this.getModalOptions(toState);
    const componentClass = toState.component !== undefined ? toState.component : modalOptions.component;
    // open modal dialog and attach modal's close handler
    const modalRef = this.modalService.open(componentClass, modalOptions, this.fetchRouteResolves(transition));
    modalRef.afterClose().subscribe((closeReason: ModalCloseReason): void => {
      // transition to previous background state
      if (closeReason === ModalCloseReason.HEADER_BUTTON ||
        closeReason === ModalCloseReason.BACKDROP_CLICK ||
        closeReason === ModalCloseReason.ESC) {
        // in case of multiple modal states (e.g. watchlist) the previous state can be a modal state,
        // but we want to go to original background page state
        const modalRouteData = this.getModalRouteData();
        const targetState = this.isModalState(previousState) ? modalRouteData.lastPageState : previousState;
        const targetStateParams = this.isModalState(previousState) ? modalRouteData.lastPageStateParams : previousStateParams;
        this.setModalRouteData({ modalRef: undefined, lastPageState: undefined, lastPageStateParams: undefined });
        this.stateService.go(targetState, targetStateParams);
      }
    });
    return modalRef;
  }

  /**
   * Make sure to use { when: 'EAGER', async: 'WAIT' } policy in your modal route definition
   * for values you want to be resolved and passed to the component displayed as a modal, e.g.:
   *   resolve: [
   *     {
   *       resolveFn: ...,
   *       deps: [...],
   *       policy: { when: 'EAGER', async: 'WAIT' }
   *     }
   *   ]
   */
  private fetchRouteResolves(transition: Transition): Record<string, unknown> {
    const result = {};
    try {
      transition.getResolveTokens()
        .filter((token: unknown): boolean => {
          const tokenIsString = typeof token === 'string';
          const tokenIsNotInternal = tokenIsString && !token.includes('$');
          const tokenIsNotTheNativeInjector = token !== 'Native Injector';

          return tokenIsString && tokenIsNotInternal && tokenIsNotTheNativeInjector;
        })
        .forEach((tokenName: string): void => {
          result[tokenName] = transition.injector().get(tokenName);
        });
    } catch (e) {
      // ignore
      // eslint-disable-next-line no-console
      console.log(e);
    }
    return result;
  }

  /**
   * Check if the state has been defined as a modal state, e.g. 'modal.messages'.
   */
  private isModalState(state: Ng2StateDeclaration): boolean {
    return this.isState(state, 'modal.');
  }

  private isState(state: Ng2StateDeclaration, statePrefix: string): boolean {
    return state !== undefined && state.name !== undefined && state.name.startsWith(statePrefix);
  }

  /**
   * Return modal's modalOptions from the route definition, undefined otherwise.
   */
  private getModalOptions(stateDefinition: StateDeclaration): ModalOptions {
    return stateDefinition.data && stateDefinition.data.modalOptions ? stateDefinition.data.modalOptions : undefined;
  }

  private getModalRouteData(): ModalRouteData {
    let parentModalRouteData;
    if (this.parent !== null && this.parent !== undefined) {
      parentModalRouteData = this.parent.getModalRouteData();
    }
    return parentModalRouteData !== undefined ? parentModalRouteData : this.modalRouteData;
  }

  private setModalRouteData(modalRouteData: ModalRouteData): void {
    if (this.parent !== null && this.parent !== undefined) {
      this.parent.setModalRouteData(modalRouteData);
    } else {
      this.modalRouteData = modalRouteData;
    }
  }
}
